import { Component, Prop, Vue } from 'vue-property-decorator'
import ConfigurationRelationshipsSteps
    from '@/modules/configurationModule/shared/components/configuration-relationships-steps/configuration-relationships-steps.vue'
import { ConfigurationAutomaticAssignmentAlgorithmResponse } from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/services/configuration-automatic-assignment-algorithm/configuration-automatic-assignment-algorithm.dto'
import {
    ConfigurationAutomaticAssignmentAlgorithmStep,
    ConfigurationAutomationPersonnelAlgorithmsDefinitionModalStepsFormState,
    initialConfigurationAutomationPersonnelAlgorithmsDefinitionModalStepsFormState
} from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/components/modal-steps-form/configuration-automation-personnel-algorithms-definition-modal-steps-form.model'
import {
    ConfigurationAutomaticAssignmentAlgorithmStepCreateRequest,
    ConfigurationAutomaticAssignmentAlgorithmStepPatchRequest,
    ConfigurationAutomaticAssignmentAlgorithmStepResponse
} from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/services/configuration-automatic-assignment-algorithm-step/configuration-automatic-assignment-algorithm-step.dto'
import { ConfigurationAutomaticAssignmentAlgorithmStepEnumResponse } from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/services/configuration-automatic-assignment-algorithm-step-type/configuration-automatic-assignment-algorithm-step-type.dto'
import { configurationAutomaticAssignmentAlgorithmStepTypeService } from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/services/configuration-automatic-assignment-algorithm-step-type/configuration-automatic-assignment-algorithm-step-type.service'
import { configurationAutomaticAssignmentAlgorithmStepService } from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/services/configuration-automatic-assignment-algorithm-step/configuration-automatic-assignment-algorithm-step.service'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { ConfigurationRelationshipStepMoved } from '@/modules/configurationModule/shared/components/configuration-relationships-steps/configuration-relationships-steps.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { GridUtils } from '@/shared/util/grid-utils'

@Component({
    components: {
        ConfigurationRelationshipsSteps
    }
})
export default class ConfigurationAutomationPersonnelAlgorithmsDefinitionModalStepsForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly automaticAssignmentAlgorithm!: ConfigurationAutomaticAssignmentAlgorithmResponse

    state: ConfigurationAutomationPersonnelAlgorithmsDefinitionModalStepsFormState
    constructor() {
        super()
        this.state = initialConfigurationAutomationPersonnelAlgorithmsDefinitionModalStepsFormState()
    }

    private async _loadAutomaticAssignmentAlgorithmStepTypes(): Promise<void> {
        this.state.automaticAssignmentAlgorithmStepTypes = await this._getAutomaticAssignmentAlgorithmStepTypes()
    }

    private async _loadAutomaticAssignmentAlgorithmSteps(): Promise<void> {
        const automaticAssignmentAlgorithmSteps: ConfigurationAutomaticAssignmentAlgorithmStepResponse[] = await this._getAutomaticAssignmentAlgorithmSteps()
        this.state.automaticAssignmentAlgorithmSteps = automaticAssignmentAlgorithmSteps.map(automaticAssignmentAlgorithmStep => ({
            x: 0,
            y: automaticAssignmentAlgorithmStep.order,
            w: 12,
            h: 1,
            i: String(automaticAssignmentAlgorithmStep.id),
            item: automaticAssignmentAlgorithmStep
        } as ConfigurationAutomaticAssignmentAlgorithmStep))
    }

    async mounted() {
        await this._loadAutomaticAssignmentAlgorithmStepTypes()
        await this._loadAutomaticAssignmentAlgorithmSteps()
    }

    /// SERVICES CALLS ///

    private async _getAutomaticAssignmentAlgorithmStepTypes(): Promise<ConfigurationAutomaticAssignmentAlgorithmStepEnumResponse[]> {
        const response = await this.$_toCallUnique(configurationAutomaticAssignmentAlgorithmStepTypeService.get())
        if (response) {
            return response
        }
        return []
    }

    private async _getAutomaticAssignmentAlgorithmSteps(): Promise<ConfigurationAutomaticAssignmentAlgorithmStepResponse[]> {
        const filterModel: CommonFilterModel = {
            'automaticAssignmentAlgorithm.id': GridUtils.getNumberFilterModel('equals', this.automaticAssignmentAlgorithm.id)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallUnique(configurationAutomaticAssignmentAlgorithmStepService.paginator(filterModel, params, this.state.laboratoryId))
        if (response) {
            return response.content
        }
        return []
    }

    private async _createAutomaticAssignmentAlgorithmStep(createRequest: ConfigurationAutomaticAssignmentAlgorithmStepCreateRequest): Promise<ConfigurationAutomaticAssignmentAlgorithmStepResponse | null> {
        const response = await this.$_toCallUnique(configurationAutomaticAssignmentAlgorithmStepService.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchAutomaticAssignmentAlgorithmStep(automaticAssignmentAlgorithmStepId: number, patchRequest: ConfigurationAutomaticAssignmentAlgorithmStepPatchRequest, showToastSuccess: boolean = true): Promise<void> {
        if (automaticAssignmentAlgorithmStepId && patchRequest) {
            await this.$_toCallUnique(configurationAutomaticAssignmentAlgorithmStepService.patch(automaticAssignmentAlgorithmStepId, this.state.laboratoryId, patchRequest))
            if (showToastSuccess) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
            }
        }
    }

    private async _deleteAutomaticAssignmentAlgorithmStep(automaticAssignmentAlgorithmStepId: number): Promise<void> {
        if (automaticAssignmentAlgorithmStepId) {
            await this.$_toCallUnique(configurationAutomaticAssignmentAlgorithmStepService.delete(automaticAssignmentAlgorithmStepId, this.state.laboratoryId))
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
        }
    }

    /// FORM OPERATIONS ///

    async createOperation(automaticAssignmentAlgorithmStepEnum: ConfigurationAutomaticAssignmentAlgorithmStepEnumResponse): Promise<void> {
        if (automaticAssignmentAlgorithmStepEnum) {
            const createRequest: ConfigurationAutomaticAssignmentAlgorithmStepCreateRequest = {
                automaticAssignmentAlgorithmId: this.automaticAssignmentAlgorithm.id,
                stepType: automaticAssignmentAlgorithmStepEnum.code
            }
            if (await this._createAutomaticAssignmentAlgorithmStep(createRequest)) {
                await this._loadAutomaticAssignmentAlgorithmSteps()
            }
        }
    }

    async deleteOperation(automaticAssignmentAlgorithmStep: ConfigurationAutomaticAssignmentAlgorithmStepResponse): Promise<void> {
        if (automaticAssignmentAlgorithmStep) {
            await this._deleteAutomaticAssignmentAlgorithmStep(automaticAssignmentAlgorithmStep.id)
            await this._loadAutomaticAssignmentAlgorithmSteps()
        }
    }

    async movedOperation(movedEvent: ConfigurationRelationshipStepMoved<ConfigurationAutomaticAssignmentAlgorithmStepResponse>): Promise<void> {
        if (movedEvent) {
            const patchRequest: ConfigurationAutomaticAssignmentAlgorithmStepPatchRequest = {
                order: movedEvent.newY + 1
            }
            await this._patchAutomaticAssignmentAlgorithmStep(movedEvent.item!.id, patchRequest, false)
            await this._loadAutomaticAssignmentAlgorithmSteps()
        }
    }
}
